import { type JwtPayload, jwtDecode } from 'jwt-decode';

import { redirectToLandlordLoginV2 } from 'src/api';
import type { IUser } from 'src/api/users-api';
import type { IStandardResponse } from 'src/common/api-utils';
import { simpleKy } from 'src/services/api/ky/simple-ky';
import { LocalStorageUtils } from 'src/utils/LocalStorageUtils';
import { getPushToken } from 'src/services/push-notifications/firebase';
import { type TempLoginData } from 'src/pages/landlord/login/LandlordLogin';

type IJwtPayload = JwtPayload & {
    // ibp = is bypass paywall
    ibp?: number;
}

export type LoginResponse = {
    access_token: string;
    refresh_token: string;
    user: IUser;
}

export type SendPasswordResetEmailResponse = {
    status: string;
    msg: string;
}

export type ChangePasswordResponse = IStandardResponse;

export default class Auth {
    static landlordLoginPath = '/landlord/login';

    /**
     * Read the currently logged-in user's access token from browser storage
     */
    static get accessToken(): string | undefined {
        return window.localStorage.getItem('access_token') || undefined;
    }

    static set accessToken(token: string | undefined) {
        if (token === undefined) {
            window.localStorage.removeItem('access_token');
        } else {
            window.localStorage.setItem('access_token', token);
        }
    }

    static get jwtPayload(): IJwtPayload | undefined {
        if (!Auth.accessToken) { return; }

        try {
            return jwtDecode(Auth.accessToken!);
        } catch (err) {
            console.error(err);
        }
    }

    static get refreshToken(): string | undefined {
        return window.localStorage.getItem('refresh_token') || undefined;
    }

    static set refreshToken(token: string | undefined) {
        if (token === undefined) {
            window.localStorage.removeItem('refresh_token');
        } else {
            window.localStorage.setItem('refresh_token', token);
        }
    }

    /**
     * Read the currently logged-in user from browser storage
     */
    static get loggedInUser(): IUser | undefined {
        return LocalStorageUtils.getItem('logged_in_user', { isJson: true }) || undefined;
    }

    static set loggedInUser(user: IUser | undefined) {
        LocalStorageUtils.setItem('logged_in_user', user ? JSON.stringify(user) : null);
    }

    static getAuthHeaders(token?: string): HeadersInit | undefined {
        if (!token) {
            return;
        }

        return {
            Authorization: `Bearer ${token}`,
            'X-User-Id': Auth.loggedInUser?.id.toString() || '',
            'X-Team-Id': Auth.loggedInUser?.leasing_team_id?.toString() || '',
        };
    }

    static redirectToLoginPage() {
        Auth.accessToken = undefined;
        redirectToLandlordLoginV2();
    }

    static async refreshAccessToken(): Promise<void> {
        const refreshToken = Auth.refreshToken;

        if (!refreshToken) {
            Auth.redirectToLoginPage();
            return;
        }

        const headers = Auth.getAuthHeaders(refreshToken);

        try {
            const _res = await simpleKy(
                'api/auth/landlord/refresh',
                { method: 'post', headers }
            );

            if (_res.ok) {
                const res = await _res.json<Pick<LoginResponse, 'access_token'>>();
                Auth.accessToken = res.access_token;
            } else {
                Auth.redirectToLoginPage();
            }
        } catch (err) {
            console.error('Failed to refresh access token', err);
        }
    }

    /**
     * This is a convenience method to save multiple pieces of login info on successful login.
     * Data is saved to browser storage.
     */
    static saveLoginInfo(loginResponse: LoginResponse) {
        Auth.accessToken = loginResponse.access_token;
        Auth.refreshToken = loginResponse.refresh_token;
        Auth.loggedInUser = loginResponse.user;
        console.debug('Logged in. User info saved to localStorage.');
    }

    static async login(email: string, password: string, saveTokens = true) {
        const res = await simpleKy('api/auth/landlord/login', {
            method: 'post',
            json: { email, password },
        }).json<LoginResponse>();

        if (saveTokens) {
            Auth.saveLoginInfo(res);
        }

        return res;
    }

    static async loginSwitchingTeam(leasingTeamId: number, tempLoginData?: TempLoginData) {
        const res = await simpleKy('api/auth/landlord/orgs/switch-active-team', {
            method: 'post',
            json: { leasing_team_id: leasingTeamId },
            headers: Auth.getAuthHeaders(tempLoginData?.access_token),
        }).json<LoginResponse>();

        Auth.saveLoginInfo(res);

        return res;
    }

    static async getUserOrgs() {
        const res = await simpleKy('api/landlord/orgs/me', {
            method: 'get',
            headers: Auth.getAuthHeaders(Auth.accessToken),
        }).json<LoginResponse>();

        return res;
    }

    static logoutWithoutRedirect() {
        Auth.unsubscribeFromPushNotifications(this.loggedInUser);
        Auth.accessToken = undefined;
        Auth.refreshToken = undefined;
        Auth.loggedInUser = undefined;
        document.dispatchEvent(new Event('reffie_logout'));
        console.debug('Logged out. User info removed from localStorage.');
    }

    static async logout() {
        Auth.logoutWithoutRedirect();
        redirectToLandlordLoginV2();
    }

    /**
     * NOTE: This method *must not* use `extendedKy` otherwise it will create a circular dependency.
     */
    static async getLoggedInUser(accessToken?: string) {
        if (!Auth.accessToken && !accessToken) {
            return undefined;
        }

        const res = await simpleKy('api/users/me', {
            headers: Auth.getAuthHeaders(accessToken || Auth.accessToken),
        }).json<IUser>();

        return res;
    }

    /**
     * @deprecated Do not use this param anymore
     */
    static async getLoggedInUserWithIngestEmailUsage() {
        if (!Auth.accessToken) {
            return undefined;
        }

        const res = await simpleKy('api/users/me?include_ingest_email_usage=true', {
            headers: Auth.getAuthHeaders(Auth.accessToken),
        }).json<IUser>();

        return res;
    }

    static sendPasswordResetEmail(email: string) {
        return simpleKy('api/users/me/password-reset/send-email', {
            method: 'post',
            json: { email },
        }).json<SendPasswordResetEmailResponse>();
    }

    static async changePassword(token: string, newPassword: string) {
        return simpleKy('api/users/me/password-reset/confirm', {
            method: 'post',
            json: { token, new_password: newPassword },
        }).json<ChangePasswordResponse>();
    }

    static unsubscribeFromPushNotifications(userData?: any) {
        // put userData into closure to avoid waiting and make it non-blocking
        (async (_userData: any) => {
            try {
                if (!_userData) {
                    return;
                }

                const token = await getPushToken(false);

                if (!token) {
                    return;
                }
                await simpleKy('api/landlord/push-notifications/unsubscribe', {
                    method: 'post',
                    json: {
                        leasing_team_id: userData.leasing_team_id,
                        user_id: userData.id,
                        token,
                    }
                });
            } catch (e) {
                console.error(e);
            }
        })(userData);
    }
}
